import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, CalendarClock, CheckCircle2, FolderKanban, MessageCircle, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useProjects } from "@/hooks/use-projects";
import { useCalendarEvents } from "@/hooks/use-calendar";
import { useInboxUnread } from "@/hooks/use-inbox-unread";
import { useLocale, formatDate } from "@/i18n/format";
import { groupUpcomingPosts } from "@/lib/upcoming-posts";
import { cn } from "@/lib/utils";
import { ProjectCard } from "@/components/projects/project-card";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AiSpendWidget } from "@/components/dashboard/ai-spend-widget";
import { AnalyticsWidget } from "@/components/dashboard/analytics-widget";
import { AccountHealthWidget } from "@/components/dashboard/account-health-widget";
import { SetupChecklist } from "@/components/dashboard/setup-checklist";
import { HomeQuickActions } from "@/components/dashboard/home-quick-actions";
import { HomeUpNext } from "@/components/dashboard/home-up-next";
import { HomeLatestMessages } from "@/components/dashboard/home-latest-messages";

const DAY_MS = 86_400_000;

/** The calendar window the home page needs: the last 7 days through the next 7. */
function homeWindow() {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    now,
    startOfToday,
    from: new Date(startOfToday.getTime() - 7 * DAY_MS),
    // End of the 7th day from today, so "this week" always covers 7 full days.
    to: new Date(startOfToday.getTime() + 8 * DAY_MS - 1),
  };
}

export function BrandOverviewPage() {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const { user } = useAuth();
  const { canManageBrand } = usePermissions();

  // Fixed for the life of the page so the query key stays stable.
  const [range] = useState(homeWindow);
  const filters = useMemo(
    () => ({ from: range.from.toISOString(), to: range.to.toISOString() }),
    [range],
  );
  const calendar = useCalendarEvents(brand.id, filters);
  const unreadQuery = useInboxUnread(brand.id);
  // null = no inbox for this brand (or it couldn't be reached); undefined = loading.
  const unread = unreadQuery.isError ? null : unreadQuery.data;

  const { upcoming, stats } = useMemo(() => {
    const events = calendar.data ?? [];
    const nowMs = range.now.getTime();
    const todayMs = range.startOfToday.getTime();
    const upcomingEvents = events.filter((e) => {
      const at = new Date(e.scheduledFor).getTime();
      // Still-pending posts from earlier today stay visible, as do today's failures.
      return at >= todayMs && (e.status === "SCHEDULED" || e.status === "FAILED");
    });
    const published = events.filter(
      (e) => e.status === "PUBLISHED" && new Date(e.publishedAt ?? e.scheduledFor).getTime() <= nowMs,
    );
    const failed = events.filter((e) => e.status === "FAILED");
    const upcomingPosts = groupUpcomingPosts(upcomingEvents);
    return {
      upcoming: upcomingPosts,
      stats: {
        scheduled: upcomingPosts.filter((p) => !p.hasFailure).length,
        published: groupUpcomingPosts(published).length,
        failed: groupUpcomingPosts(failed).length,
      },
    };
  }, [calendar.data, range]);

  const hour = range.now.getHours();
  const period = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const unreadTotal = unread ? unread.dms + unread.comments : null;

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-7xl mx-auto w-full space-y-6">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {formatDate(range.now, locale, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="text-2xl font-bold tracking-tight">
          {firstName ? t(`home.greeting.${period}`, { name: firstName }) : brand.name}
        </h1>
        <p className="text-sm text-muted-foreground">{t("home.subtitle", { brand: brand.name })}</p>
      </header>

      <SetupChecklist />

      <HomeQuickActions brandSlug={brand.slug} unreadTotal={unread === undefined ? 0 : unreadTotal} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          to={`/${brand.slug}/calendar`}
          icon={CalendarClock}
          label={t("home.stats.scheduled")}
          value={calendar.isLoading ? null : stats.scheduled}
        />
        <StatTile
          to={`/${brand.slug}/calendar`}
          icon={CheckCircle2}
          label={t("home.stats.published")}
          value={calendar.isLoading ? null : stats.published}
        />
        <StatTile
          to={`/${brand.slug}/calendar`}
          icon={AlertCircle}
          label={t("home.stats.failed")}
          value={calendar.isLoading ? null : stats.failed}
          alert={stats.failed > 0}
        />
        {unread !== null && (
          <StatTile
            to={`/${brand.slug}/inbox`}
            icon={MessageCircle}
            label={t("home.stats.unread")}
            value={unread === undefined ? null : (unreadTotal ?? 0)}
            highlight={(unreadTotal ?? 0) > 0}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <HomeUpNext
            brandSlug={brand.slug}
            posts={upcoming}
            isLoading={calendar.isLoading}
            isError={calendar.isError}
            onRetry={() => void calendar.refetch()}
          />
        </div>
        <HomeLatestMessages
          brandId={brand.id}
          brandSlug={brand.slug}
          unread={unread}
          canManageBrand={canManageBrand}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AiSpendWidget />
        <AnalyticsWidget />
        <AccountHealthWidget />
      </div>

      <ProjectsSection />
    </div>
  );
}

interface StatTileProps {
  to: string;
  icon: LucideIcon;
  label: string;
  /** null while loading. */
  value: number | null;
  alert?: boolean;
  highlight?: boolean;
}

function StatTile({ to, icon: Icon, label, value, alert, highlight }: StatTileProps) {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-card p-4 transition hover:bg-muted/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        alert && "border-destructive/40 bg-destructive/5",
      )}
    >
      <Icon
        className={cn(
          "h-5 w-5 shrink-0",
          alert ? "text-destructive" : highlight ? "text-primary" : "text-muted-foreground",
        )}
        aria-hidden="true"
      />
      <span className="min-w-0">
        {value === null ? (
          <Skeleton className="mb-1 h-6 w-8" />
        ) : (
          <span className={cn("block text-2xl font-bold leading-none tabular-nums", alert && "text-destructive")}>
            {value}
          </span>
        )}
        <span className="mt-1 block text-xs text-muted-foreground leading-snug">{label}</span>
      </span>
    </Link>
  );
}

/** The brand's projects, filterable by pipeline stage. */
function ProjectsSection() {
  const { t } = useTranslation("dashboard");
  const { t: tNav } = useTranslation("nav");
  const navigate = useNavigate();
  const { brand } = useBrandContext();
  const [stageFilter, setStageFilter] = useState("ALL");

  const stage = stageFilter === "ALL" ? undefined : stageFilter;
  const { data: projects, isLoading } = useProjects(brand.id, stage);

  const STAGE_TABS: Array<{ value: string; label: string }> = [
    { value: "ALL", label: t("stage.all") },
    { value: "BRAINSTORM", label: t("stage.brainstorm") },
    { value: "BRIEFING", label: t("stage.briefing") },
    { value: "GENERATING", label: t("stage.generating") },
    { value: "REVIEW", label: t("stage.review") },
    { value: "SCHEDULING", label: t("stage.scheduling") },
    { value: "PUBLISHED", label: t("stage.published") },
  ];

  return (
    <section aria-labelledby="home-projects" className="space-y-4">
      <h2 id="home-projects" className="text-lg font-semibold">
        {t("home.projects.title")}
      </h2>

      <div className="overflow-x-auto">
        <Tabs value={stageFilter} onValueChange={setStageFilter}>
          <TabsList>
            {STAGE_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : !projects?.length ? (
        <EmptyState
          icon={FolderKanban}
          title={t("overview.emptyTitle")}
          description={t("overview.emptyDesc")}
          action={{
            label: tNav("create.post"),
            onClick: () => navigate(`/${brand.slug}/quick-post`),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </section>
  );
}
